/*
 *
 *  * Copyright (c) [hoarfroster-frontend] 2026. All Rights Reserved.
 *  *
 *  * Last Modified on Jun 21, 2026 by hoarfroster
 *
 */

import { useEffect, useState } from 'react';
import { applyTheme, getCurrentTheme, getThemeColors, setAutoMode, ThemeMode } from '@/utils/theme';
import { ThemeSelector } from '@components/theme-preview';
import I18N from '@/utils/i18n.base';

/**
 * ThemeSwitcher component allows users to toggle between light and dark themes,
 * and optionally follow the system color scheme.
 */
export default function ThemeSwitcher() {
    const initialTheme = getCurrentTheme();
    const [autoMode, setAutoModeState] = useState<boolean>(initialTheme.autoMode);
    const [mode, setMode] = useState<ThemeMode>(initialTheme.mode);
    const [themeColor, setThemeColor] = useState<ThemeMode>(initialTheme.mode);

    // Automatically switch theme when following system
    useEffect(() => {
        if (!autoMode) return;
        const mq = window.matchMedia('(prefers-color-scheme: dark)');
        const syncMode = (nextMode: ThemeMode) => {
            setMode(nextMode);
            setThemeColor(nextMode);
            applyTheme(nextMode);
        };

        syncMode(mq.matches ? 'dark' : 'light');
        const handler = (e: MediaQueryListEvent) => syncMode(e.matches ? 'dark' : 'light');
        mq.addEventListener('change', handler);
        return () => mq.removeEventListener('change', handler);
    }, [autoMode]);

    // Store settings and apply theme
    useEffect(() => {
        setAutoMode(autoMode);
    }, [autoMode]);

    useEffect(() => {
        applyTheme(mode);
    }, [mode]);

    // Toggle follow system mode
    const handleAutoModeChange = (checked: boolean) => {
        setAutoModeState(checked);
        if (checked) {
            const mq = window.matchMedia('(prefers-color-scheme: dark)');
            const systemMode = mq.matches ? 'dark' : 'light';
            setMode(systemMode);
            setThemeColor(systemMode);
            applyTheme(systemMode);
        } else {
            applyTheme(mode);
        }
    };

    // Prepare theme list for ThemeSelector
    const themes = ['light', 'dark'].map((mode) => getThemeColors(mode as ThemeMode));

    // Remove Switch and ThemePreview usage, use ThemeSelector instead
    return (
        <ThemeSelector
            themes={themes}
            selectedTheme={themeColor}
            onThemeChange={(color) => {
                setAutoModeState(false);
                setThemeColor(color as ThemeMode);
                setMode(color as ThemeMode);
                applyTheme(color as ThemeMode);
            }}
            autoMode={autoMode}
            onAutoModeChange={handleAutoModeChange}
            followSystemLabel={I18N.app.followSystemTheme}
            themeColorLabel={I18N.app.themeColor}
        />
    );
}
